<script>
    import {tacticsPlayers} from '../store';
    import {createEventDispatcher, onMount, onDestroy} from "svelte";
    import {HistoryEvent} from "../tactics.model";
    import {HistoryEventType} from "../history-events.enum";

    const dispatch = createEventDispatcher();

    let rounds = ['20', '19', '18', '17', '16', '15', '25'];
    let gameFinished = false;
    let activePlayerIndex = 0;
    let history = [];

    function reset() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('reset');
    }

    function newGame() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        dispatch('newGame');
    }

    function addScore(segment) {
        if (gameFinished) return;
        let activePlayer = $tacticsPlayers[activePlayerIndex];
        if (!activePlayer) return;
        let segmentData = activePlayer.rounds.filter(item => item.segment === segment)[0];
        if (!segmentData) return;

        if (segmentData.hits < 3) {
            segmentData.hits++;
            history = [...history, new HistoryEvent(HistoryEventType.SEGMENT_HIT, activePlayerIndex, parseInt(segment))];
        } else {
            if (!isRoundFull(segment)) {
                $tacticsPlayers[activePlayerIndex].totalScore = $tacticsPlayers[activePlayerIndex].totalScore + parseInt(segment);
                history = [...history, new HistoryEvent(HistoryEventType.POINTS_ADDED, activePlayerIndex, parseInt(segment))];
            }
        }

        activePlayer.rounds = [...activePlayer.rounds];

        tacticsPlayers.update(tacticsPlayers => {
            if (tacticsPlayers[activePlayerIndex]) {
                tacticsPlayers[activePlayerIndex].rounds = [...tacticsPlayers[activePlayerIndex].rounds];
            }
            return [...tacticsPlayers];
        });

        checkWinner();
    }

    function checkWinner() {
        let activePlayer = $tacticsPlayers[activePlayerIndex];
        if (!activePlayer) return;
        let unfinishedRounds = activePlayer.rounds.filter(round => round.hits < 3);

        if (unfinishedRounds.length === 0) {
            let ranking = $tacticsPlayers.map((x) => x);
            ranking.sort((player1, player2) => player1.totalScore - player2.totalScore).reverse();
            if (ranking[0].name === activePlayer.name) {
                gameFinished = true;
            }
        }
    }

    function nextPlayer() {
        if (gameFinished) return;
        if (activePlayerIndex === $tacticsPlayers.length - 1) {
            activePlayerIndex = 0;
        } else {
            activePlayerIndex = activePlayerIndex + 1;
        }

        history = [...history, new HistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0)];
    }

    function undo() {
        gameFinished = false;
        let lastHistoryItem = history[history.length - 1];
        if (!lastHistoryItem) return;
        let player = $tacticsPlayers[lastHistoryItem.playerIndex];
        let segment = String(lastHistoryItem.segment);

        if (lastHistoryItem.type === HistoryEventType.POINTS_ADDED && player) {
            player.totalScore = player.totalScore - parseInt(segment);
        }

        if (lastHistoryItem.type === HistoryEventType.SEGMENT_HIT && player) {
            let roundSegment = player.rounds.filter(item => item.segment === segment)[0];
            if (roundSegment && roundSegment.hits > 0) {
                roundSegment.hits = roundSegment.hits - 1;
            }
        }

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            if (activePlayerIndex === 0) {
                activePlayerIndex = $tacticsPlayers.length - 1;
            } else {
                activePlayerIndex = activePlayerIndex - 1;
            }
        }

        history = history.slice(0, -1);
        tacticsPlayers.update(tacticsPlayers => {
            if (tacticsPlayers[activePlayerIndex]) {
                tacticsPlayers[activePlayerIndex].rounds = [...tacticsPlayers[activePlayerIndex].rounds];
            }
            return [...tacticsPlayers];
        });
    }

    function isRoundFull(round) {
        let roundIsFull = true;
        $tacticsPlayers.forEach(player => {
            let item = player.rounds.filter(item => item.segment === round)[0];
            if (!item || item.hits < 3) {
                roundIsFull = false;
            }
        });

        return roundIsFull;
    }

    function isPlayerActive(player, finished = gameFinished, activeIdx = activePlayerIndex) {
        return !finished && $tacticsPlayers[activeIdx]?.name === player?.name;
    }

    function isPlayerWinner(player, finished = gameFinished, activeIdx = activePlayerIndex) {
        return finished && $tacticsPlayers[activeIdx]?.name === player?.name;
    }

    function getPlayerHits(player, segment) {
        const item = player.rounds.find(r => r.segment === segment);
        return item ? item.hits : 0;
    }

    function isRoundClosedForEveryone(round, players = $tacticsPlayers) {
        if (!players || players.length === 0) return false;
        let roundIsFull = true;
        players.forEach(player => {
            let item = player.rounds?.find(item => item.segment === round);
            if (!item || item.hits < 3) {
                roundIsFull = false;
            }
        });
        return roundIsFull;
    }

    function canAddScore(round, players = $tacticsPlayers, finished = gameFinished) {
        if (finished) return false;
        return !isRoundClosedForEveryone(round, players);
    }

    function handleKeydown(event) {
        if (event.key === 'Enter') {
            event.preventDefault();
            nextPlayer();
        }
    }

    onMount(() => {
        window.addEventListener('keydown', handleKeydown);
    });

    onDestroy(() => {
        window.removeEventListener('keydown', handleKeydown);
    });
</script>

<div class="tactics-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border bg-[#f0ece9]">
    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto bg-white rounded-2xl sm:rounded-3xl shadow-sm px-4 sm:px-6 py-3 mb-3 sm:mb-4 border border-black/5 flex flex-wrap items-center justify-between gap-4 shrink-0">
        <!-- Brand & Game Badge -->
        <div class="flex items-center gap-4 sm:gap-6 min-w-[200px]">
            <button class="inline-flex items-center transition-opacity hover:opacity-85 focus:outline-none" on:click={newGame} title="Yuma Home">
                <img alt="Yuma Logo" class="h-8 sm:h-9 w-auto object-contain" src="images/yuma.png" />
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Center Controls: Next Player -->
        <div class="flex items-center justify-center flex-1 max-w-md mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner w-full sm:w-auto justify-center">
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#eba9ff] hover:bg-[#e493fb] active:scale-[0.98] text-[#005d46] font-bold px-6 sm:px-8 rounded-xl transition-all shadow-sm border border-purple-300/30 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={gameFinished}
                    on:click={nextPlayer}
                    type="button"
                    title="Next player (shortcut: Enter)"
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[200px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={history.length === 0}
                on:click={undo}
                title="Undo last action"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                on:click={reset}
                title="Reset game"
                type="button"
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                on:click={newGame}
                title="Exit to Setup"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Main Table Container -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 min-h-0 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-black/5 flex flex-col overflow-hidden">
        <div class="flex-1 min-h-0 overflow-auto">
            <table class="tactics-table w-full h-full flex-1 border-collapse select-none">
                <!-- THEAD: Players Header -->
                <thead>
                    <tr class="h-12 sm:h-14 text-white text-lg">
                        <th class="w-48 sm:w-60 md:w-72 bg-[#005d46] text-white p-3 sm:p-4 text-left align-middle border-r border-emerald-800/40" style="text-align: left;">
                            <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">style</span>
                                <div class="text-left">
                                    <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                        Tactics
                                    </div>
                                    <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                        3 hits to close
                                    </div>
                                </div>
                            </div>
                        </th>

                        <!-- Dynamic Player Columns (Header) -->
                        {#each $tacticsPlayers as player}
                            <th
                                class="px-4 py-2.5 align-middle text-center border-r last:border-r-0 transition-colors {
                                    isPlayerWinner(player, gameFinished, activePlayerIndex)
                                        ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                        : isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                }"
                                style="width: {80 / ($tacticsPlayers.length || 1)}%;"
                            >
                                <div class="flex items-center justify-center gap-2 flex-wrap">
                                    {#if isPlayerActive(player, gameFinished, activePlayerIndex) && !isPlayerWinner(player, gameFinished, activePlayerIndex)}
                                        <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                    {/if}
                                    <span class="text-xl sm:text-2xl font-bold font-headline truncate">
                                        {player.name}
                                    </span>
                                    {#if isPlayerWinner(player, gameFinished, activePlayerIndex)}
                                        <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                            🏆
                                        </span>
                                    {/if}
                                </div>
                            </th>
                        {/each}
                    </tr>

                    <!-- Score Row -->
                    <tr class="h-10 sm:h-12 border-b border-slate-200 bg-stone-50/90 text-slate-800">
                        <th class="w-[20%] min-w-[160px] px-5 py-2 text-left font-bold text-xs sm:text-sm tracking-wider uppercase text-slate-500 border-r border-slate-200 align-middle">
                            <div class="flex items-center gap-2">
                                <span class="material-symbols-outlined text-[18px] text-slate-400">scoreboard</span>
                                <span>Total Points</span>
                            </div>
                        </th>
                        {#each $tacticsPlayers as player}
                            <th
                                class="px-4 py-2 align-middle border-r last:border-r-0 border-slate-200 transition-colors {
                                    isPlayerWinner(player, gameFinished, activePlayerIndex)
                                        ? 'bg-emerald-50 text-[#005d46]'
                                        : isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-amber-50/50 text-slate-900'
                                            : 'text-slate-800'
                                }"
                            >
                                <div class="flex items-center justify-center w-full h-full text-center">
                                    <span class="font-headline font-black text-xl sm:text-2xl md:text-3xl text-slate-900 tracking-tight leading-none text-center">
                                        {player.totalScore}
                                    </span>
                                </div>
                            </th>
                        {/each}
                    </tr>
                </thead>

                <!-- TBODY: Segment Rows -->
                <tbody>
                    {#each rounds as round, roundIdx}
                        <tr class="segment-row transition-colors border-b last:border-b-0 border-slate-200/80 {
                            roundIdx % 2 === 0 ? 'bg-white hover:bg-[#f5f3f0]' : 'bg-[#fbfaf8] hover:bg-[#f5f3f0]'
                        }">
                            <!-- Left: Segment Button Column -->
                            <td class="segment-label text-left px-3 sm:px-4 py-1.5 align-middle border-r border-slate-200">
                                <button
                                    class="w-full h-11 sm:h-12 inline-flex items-center justify-between px-4 rounded-xl font-headline font-black text-lg sm:text-xl transition-all shadow-sm border cursor-pointer active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed {
                                        isRoundClosedForEveryone(round, $tacticsPlayers)
                                            ? 'bg-red-50 hover:bg-red-100 text-red-700 border-red-200'
                                            : 'bg-[#6434da] hover:bg-[#5229b5] text-white border-purple-800/40'
                                    }"
                                    disabled={!canAddScore(round, $tacticsPlayers, gameFinished)}
                                    on:click={() => addScore(round)}
                                    title="Hit segment {round === '25' ? 'Bull' : round}"
                                    type="button"
                                >
                                    <span>{round === '25' ? 'Bull' : round}</span>
                                    <span class="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-md {
                                        isRoundClosedForEveryone(round, $tacticsPlayers)
                                            ? 'bg-red-200/60 text-red-800'
                                            : 'bg-purple-900/50 text-purple-200'
                                    }">
                                        {isRoundClosedForEveryone(round, $tacticsPlayers) ? 'Closed' : 'Hit'}
                                    </span>
                                </button>
                            </td>

                            <!-- Players' Segment Status -->
                            {#each $tacticsPlayers as player}
                                <td
                                    class="py-1 sm:py-2 align-middle text-center border-r last:border-r-0 border-slate-200/50 transition-all {
                                        isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-amber-50/20'
                                            : ''
                                    }"
                                >
                                    <div class="flex items-center justify-center w-full h-full min-h-[38px] sm:min-h-[46px]">
                                        {#if getPlayerHits(player, round) === 0}
                                            <span class="text-slate-300 font-bold text-lg sm:text-xl leading-none select-none">-</span>
                                        {:else if getPlayerHits(player, round) === 1}
                                            <img
                                                alt="1 hit"
                                                class="h-9 sm:h-11 md:h-12 w-auto object-contain transition-transform hover:scale-105"
                                                src="images/tactics/tactics-one.png"
                                            />
                                        {:else if getPlayerHits(player, round) === 2}
                                            <img
                                                alt="2 hits"
                                                class="h-9 sm:h-11 md:h-12 w-auto object-contain transition-transform hover:scale-105"
                                                src="images/tactics/tactics-two.png"
                                            />
                                        {:else if getPlayerHits(player, round) >= 3}
                                            <img
                                                alt={isRoundClosedForEveryone(round, $tacticsPlayers) ? "Closed for all" : "3 hits"}
                                                class="h-9 sm:h-11 md:h-12 w-auto object-contain transition-transform hover:scale-105"
                                                src={isRoundClosedForEveryone(round, $tacticsPlayers) ? "images/tactics/tactics-full-red.png" : "images/tactics/tactics-full.png"}
                                            />
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Sticky Footer Informational Strip -->
        <footer class="bg-stone-50 border-t border-slate-200 px-4 sm:px-6 py-2.5 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-2 shrink-0">
            <div class="flex items-center gap-2">
                <span class="w-2 h-2 rounded-full bg-[#005d46]"></span>
                <span class="font-medium">Hit 3 times to open scoring. When all players have 3 hits, the segment is closed.</span>
            </div>
            <div class="flex items-center gap-3 font-medium">
                <span class="flex items-center gap-1">
                    <kbd class="px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-700 font-mono shadow-2xs text-[10px]">Enter</kbd>
                    Next player
                </span>
            </div>
        </footer>
    </main>
    <!-- END: Main Table Container -->
</div>

<style>
    .tactics-screen {
        font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .font-headline {
        font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    .tactics-table {
        table-layout: fixed;
        width: 100%;
        border-collapse: collapse;
    }

    .tactics-table th,
    .tactics-table td {
        vertical-align: middle !important;
    }

    .segment-row {
        height: 1%;
    }
</style>
